import {
  assignmentKey,
  type PersonnelAssignment,
} from "./matter-personnel-assignment-diff";
import { type Matter } from "@/lib/types";

export function teamRemovalMembers(
  matter: Matter,
  teamId: string,
  newTeamMembers: PersonnelAssignment[],
): PersonnelAssignment[] {
  const connection = matter.teamConnections.find(
    (item) => item.teamId === teamId && !item.dateTimeEnd,
  );
  if (!connection) return newTeamMembers;
  return [
    ...new Map(
      [
        ...newTeamMembers,
        ...(connection.teammates ?? []).map((member) => ({
          userId: member.userId,
          roleId: member.roleId,
        })),
        ...matter.assignees
          .filter(
            (assignee) =>
              !assignee.dateTimeEnd &&
              assignee.teamConnectionId === connection.id,
          )
          .map((assignee) => ({
            userId: assignee.user.id,
            roleId: assignee.role.id,
          })),
      ].map((member) => [assignmentKey(member), member]),
    ).values(),
  ];
}

export function assignmentsAfterTeamRemoval({
  matter,
  teamId,
  initial,
  edited,
  members,
  otherTeamMembers,
  removeAssignees,
}: {
  matter: Matter;
  teamId: string;
  initial: PersonnelAssignment[];
  edited: PersonnelAssignment[];
  members: PersonnelAssignment[];
  otherTeamMembers: PersonnelAssignment[];
  removeAssignees: boolean;
}): PersonnelAssignment[] {
  const connection = matter.teamConnections.find(
    (item) => item.teamId === teamId && !item.dateTimeEnd,
  );
  if (!removeAssignees) {
    if (connection) return edited;
    const existingKeys = new Set(edited.map(assignmentKey));
    return [
      ...edited,
      ...members.filter((item) => !existingKeys.has(assignmentKey(item))),
    ];
  }

  const memberUsers = new Set(members.map((item) => item.userId));
  const protectedKeys = new Set([
    ...matter.assignees
      .filter(
        (item) =>
          !item.dateTimeEnd &&
          (!connection || item.teamConnectionId !== connection.id),
      )
      .map((item) =>
        assignmentKey({ userId: item.user.id, roleId: item.role.id }),
      ),
    ...otherTeamMembers.map(assignmentKey),
  ]);
  const remaining = edited.filter(
    (item) =>
      !memberUsers.has(item.userId) || protectedKeys.has(assignmentKey(item)),
  );
  const remainingKeys = new Set(remaining.map(assignmentKey));
  return [
    ...remaining,
    ...initial.filter(
      (item) =>
        memberUsers.has(item.userId) &&
        protectedKeys.has(assignmentKey(item)) &&
        !remainingKeys.has(assignmentKey(item)),
    ),
  ];
}

export function restoreTeamAssignments(
  current: PersonnelAssignment[],
  removed: PersonnelAssignment[],
): PersonnelAssignment[] {
  const assignedKeys = new Set(current.map(assignmentKey));
  return [
    ...current,
    ...removed.filter((item) => !assignedKeys.has(assignmentKey(item))),
  ];
}

export function withoutRemovedTeam<T>(
  current: Record<string, T>,
  teamId: string,
) {
  return Object.fromEntries(
    Object.entries(current).filter(([id]) => id !== teamId),
  ) as Record<string, T>;
}

export function planTeamRemoval({
  matter,
  teamId,
  initial,
  edited,
  members,
  selectedTeamIds,
  roster,
  removeAssignees,
}: {
  matter: Matter;
  teamId: string;
  initial: PersonnelAssignment[];
  edited: PersonnelAssignment[];
  members: PersonnelAssignment[];
  selectedTeamIds: string[];
  roster: { teamId: string; userId: string; roleId: string }[];
  removeAssignees: boolean;
}) {
  const otherTeamMembers = [
    ...matter.teamConnections
      .filter(
        (team) =>
          !team.dateTimeEnd &&
          team.teamId !== teamId &&
          team.teamId &&
          selectedTeamIds.includes(team.teamId),
      )
      .flatMap((team) => team.teammates ?? []),
    ...roster.filter(
      (member) =>
        member.teamId !== teamId && selectedTeamIds.includes(member.teamId),
    ),
  ].map((member) => ({ userId: member.userId, roleId: member.roleId }));
  const next = assignmentsAfterTeamRemoval({
    matter,
    teamId,
    initial,
    edited,
    members,
    otherTeamMembers,
    removeAssignees,
  });
  const nextKeys = new Set(next.map(assignmentKey));
  return {
    assignments: next,
    removed: removeAssignees
      ? edited.filter((item) => !nextKeys.has(assignmentKey(item)))
      : [],
  };
}
